import { useAuthenticator } from "@aws-amplify/ui-react";
import { useEffect, useState } from "react";
import { Button, Spinner } from "react-bootstrap";
import { DollarSign, Info, Lock, Power } from "react-feather";
import { client } from "../App";
import { config } from "../config";
import { useProfileContext } from "../contexts/ProfileContexts";
import { createWaitlistEntry } from "../graphql/mutations";
import {
  createCheckoutSessionForDevicePurchase,
  getWaitlistStatus,
} from "../graphql/queries";
import { refreshMe } from "../utils";

const { prices } = config;

interface AppsyncError {
  data: any;
  errors: {
    path: string[];
    data: any;
    errorType: string;
    errorInfo: string;
    locations: { line: number; column: number }[];
    message: string;
  }[];
}
interface GetWaitlistStatusReturn {
  data: {
    getWaitlistStatus: {
      howManyAhead: number;
      currentlyInLine: boolean;
      stockOnHand: boolean;
    };
  };
}

const Purchase = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [stockOnHand, setStockOnHand] = useState(false);
  const [currentlyInLine, setCurrentlyInLine] = useState(false);
  const [howManyAhead, setHowManyAhead] = useState(1);
  const [fetchedWaitlist, setFetchedWaitlist] = useState(false);

  const useAuthenticatorReturn = useAuthenticator();

  const cognitoUser = useAuthenticatorReturn.user;
  const { setProfile, setUser, user } = useProfileContext();
  useEffect(() => {
    console.log("cognito user", cognitoUser);
    if (cognitoUser && !user) {
      refreshMe(setUser, setProfile);
    }
  }, [cognitoUser, setProfile, setUser]);

  useEffect(() => {
    if (user && !fetchedWaitlist) {
      fetchWaitlistStatus();
      setFetchedWaitlist(true);
    }
  }, [user, fetchedWaitlist]);

  const fetchWaitlistStatus = async () => {
    try {
      const { data } = (await client.graphql({
        query: getWaitlistStatus,
      })) as GetWaitlistStatusReturn;
      console.log("fetchWaitlistStatus", { data });
      setStockOnHand(data.getWaitlistStatus.stockOnHand);
      setCurrentlyInLine(data.getWaitlistStatus.currentlyInLine);
      setHowManyAhead(data.getWaitlistStatus.howManyAhead);
      return data.getWaitlistStatus;
    } catch (error) {
      console.error(error);
    }
  };

  const handlePurchaseClick = async () => {
    setLoading(true);
    try {
      const priceId = prices.find((p) => p.name === "device")?.priceId;
      console.log({ priceId });
      const { data } = (await client.graphql({
        query: createCheckoutSessionForDevicePurchase,
        variables: { priceId },
      })) as any;
      console.log("handlePurchaseClick", { data });
      window.location.href = data.createCheckoutSessionForDevicePurchase.url;
    } catch (error) {
      setLoading(false);
      setError(
        `Error purchasing device: ${JSON.stringify(
          (error as AppsyncError).errors[0].message
        )}`
      );
      console.error(error);
    }
  };

  const handleCreateWaitlistEntry = async () => {
    setLoading(true);
    try {
      const { data } = (await client.graphql({
        query: createWaitlistEntry,
      })) as any;
      console.log({ data });
      await fetchWaitlistStatus();
    } catch (error) {
      setLoading(false);
      setError(
        `Error joining waitlist: ${JSON.stringify(
          (error as AppsyncError).errors[0].message
        )}`
      );
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const renderPurchaseOptions = () => {
    if (user && user.deviceOrders && user.deviceOrders.length > 0) {
      return (
        <div className="alert alert-success" role="alert">
          <p>
            You purchased a device. The order came through at{" "}
            {user.deviceOrders[0].timestamp}.
          </p>
          <p>
            Its status is {user.deviceOrders[0].status}.{" "}
            {user.deviceOrders[0].status !== "completed" && (
              <span>
                You can expect it to arrive within 10 business days of your
                order.
              </span>
            )}
          </p>
          {user.deviceOrders[0].trackingNumber && (
            <p>The tracking number is {user.deviceOrders[0].trackingNumber}.</p>
          )}
        </div>
      );
    }

    if (
      (user &&
        user.deviceOrders &&
        user.deviceOrders.length === 0 &&
        stockOnHand) ||
      (currentlyInLine && howManyAhead <= 0)
    ) {
      return (
        <div className="alert alert-info" role="alert">
          <h5>Devices are in stock - order yours now!</h5>
          <p>
            <Button
              variant="primary"
              onClick={handlePurchaseClick}
              disabled={loading}
            >
              Purchase{" "}
              {user?.deviceOrders && user?.deviceOrders.length > 0
                ? "Another"
                : ""}{" "}
              Blu View 5
            </Button>
            {loading && <Spinner animation="border" variant="success" />}
          </p>
        </div>
      );
    }

    if (
      user &&
      user.deviceOrders &&
      user.deviceOrders.length === 0 &&
      !stockOnHand &&
      !currentlyInLine
    ) {
      return (
        <div className="alert alert-info" role="alert">
          <p>
            We are currently out of stock. Please join the waitlist to be
            notified when we have more devices available.
          </p>
          <p>
            <Button
              variant="primary"
              onClick={handleCreateWaitlistEntry}
              disabled={loading}
            >
              Join Waitlist
            </Button>
            {loading && <Spinner animation="border" variant="success" />}
          </p>
        </div>
      );
    }

    if (
      user &&
      user.deviceOrders &&
      user.deviceOrders.length === 0 &&
      currentlyInLine &&
      howManyAhead > 0
    ) {
      return (
        <div className="alert alert-info" role="alert">
          <p>
            You are currently in line to purchase a device.{" "}
            {howManyAhead === 1 ? (
              <span>You're next in line</span>
            ) : (
              <span>There are {howManyAhead} people ahead of you.</span>
            )}
          </p>
        </div>
      );
    }
  };

  return (
    <div>
      <div className="mt-4">
        <h1 className="d-flex align-items-center gap-2">
          Skip the setup and get mining!
        </h1>
      </div>

      <div className="d-flex gap-4 mt-4">
        <img src="/images/BlueViewAngle.png" alt="phone angle view" />
        <img src="/images/BlueViewSide.png" alt="phone side view" />
        <img src="/images/BlueViewBack.png" alt="phone back view" />
      </div>

      <div className="mt-4">
        <h4>
          <Info size={28} /> How does it work?
        </h4>
      </div>
      <p className="mt-3">
        We'll send you a{" "}
        <a
          href="https://www.bluproducts.com/devices/view-5/index.html"
          target="_new"
        >
          Blu View 5
        </a>{" "}
        set up with all of the software you need to get mining. The device costs{" "}
        <strong>$49.99</strong>. When you buy one,{" "}
        <strong>
          <em>we'll give you a matching $49.99</em>
        </strong>{" "}
        off of your first subscription. You will still need to sign up for a
        paid stubminer plan to use the device.
      </p>

      <div className="mt-4">
        <h4>Once your device arrives</h4>
        <ol>
          <li className="mb-2">
            Unbox your device and power it on <Power />
          </li>
          <li className="mb-2">
            Log in to your MLB The Show account <Lock />
          </li>
          <li className="mb-2">
            Start the automate script to start mining stubs <DollarSign />{" "}
            <DollarSign /> <DollarSign />
          </li>
        </ol>
      </div>

      {renderPurchaseOptions()}
      {error && <p>{error}</p>}
    </div>
  );
};

export default Purchase;
